// Supervisor Analytics — one supervisor's full window.
//
// Opened by clicking a supervisor on the Supervisors screen; arrives with the
// same window that screen had set (via query params) so the numbers here are
// the numbers just clicked. Every figure follows the filter, and the Excel
// button downloads exactly what is on screen: summary, every deal they priced
// or corrected (customer, car, every part), and a day-by-day rollup.

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiBarChart2,
  FiCalendar,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiDownload,
  FiEdit3,
  FiMail,
  FiMessageCircle,
  FiPercent,
  FiPhone,
  FiRefreshCw,
  FiTrendingUp,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { supervisorApi } from '../api/supervisorApi';
import { downloadSupervisorWorkbook } from '../utils/teamExcel';

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const fmtMinutes = (m) => {
  if (m === null || m === undefined || m === '') return '—';
  const mins = Math.round(Number(m));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const STAGE_PILL = {
  pending: 'bg-amber-100 text-amber-700',
  live: 'bg-sky-100 text-sky-700',
  waiting: 'bg-violet-100 text-violet-700',
  hold: 'bg-slate-100 text-slate-600',
  won: 'bg-emerald-100 text-emerald-700',
  loss: 'bg-rose-100 text-rose-600',
};
const STAGE_LABEL = {
  pending: 'Pending', live: 'Live', waiting: 'Waiting',
  hold: 'On hold', won: 'Sold', loss: 'Lost',
};

const StagePill = ({ stage }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold capitalize ${STAGE_PILL[stage] || 'bg-slate-100 text-slate-600'}`}>
    {STAGE_LABEL[stage] || stage}
  </span>
);

const RolePill = ({ role }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${
    role === 'priced' ? 'bg-indigo-50 text-indigo-600' : 'bg-violet-50 text-violet-600'
  }`}>
    {role === 'priced' ? 'Priced' : 'Corrected'}
  </span>
);

const StatChip = ({ icon: Icon, iconBg, iconColor, label, value, valueColor = 'text-slate-800' }) => (
  <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm flex-1 min-w-[110px]">
    <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-slate-400 leading-none mb-0.5">{label}</p>
      <p className={`text-lg font-bold leading-none ${valueColor}`}>{value}</p>
    </div>
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-24">
    <div className="h-9 w-9 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
  </div>
);

const PERIOD_CHIPS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Date range' },
];

const SupervisorAnalyticsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Arrive on the same window the Supervisors screen had set.
  const initial = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get('from') || qs.get('to')) {
      return { period: 'custom', from: qs.get('from') || '', to: qs.get('to') || '' };
    }
    return { period: qs.get('period') || 'today', from: '', to: '' };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [period, setPeriod] = useState(initial.period);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const windowParams = useMemo(() => {
    if (period === 'custom') {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      return params;
    }
    return { period };
  }, [period, dateFrom, dateTo]);

  useEffect(() => {
    if (period === 'custom' && !dateFrom && !dateTo) return undefined;
    let alive = true;
    setLoading(true);
    setError('');
    supervisorApi.getAnalytics(id, windowParams)
      .then((res) => { if (alive) setData(res.data); })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.error || 'Could not load this supervisor');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, windowParams, period, dateFrom, dateTo]);

  const sup = data?.supervisor;
  const s = data?.summary || {};
  const rows = data?.inquiries || [];
  const windowLabel = period === 'custom'
    ? `${dateFrom || 'the beginning'} → ${dateTo || 'today'}`
    : PERIOD_CHIPS.find((p) => p.id === period)?.label || period;

  return (
    <MainLayout>
      <div className="space-y-5">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-3 animate-fade-in-up">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/supervisors')}
              className="w-10 h-10 shrink-0 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-violet-600 hover:border-violet-300 transition-all"
              title="Back to supervisors"
            >
              <FiArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
              {(sup?.name || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 truncate">{sup?.name || 'Supervisor'}</h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                {sup?.phone_number && (
                  <span className="flex items-center gap-1"><FiPhone className="w-3 h-3" />{sup.phone_number}</span>
                )}
                {sup?.email && (
                  <span className="flex items-center gap-1"><FiMail className="w-3 h-3" />{sup.email}</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => data && downloadSupervisorWorkbook(data)}
            disabled={!data || loading || (period === 'custom' && !dateFrom && !dateTo)}
            title={`Download this file — ${windowLabel}`}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-all active:scale-95 disabled:opacity-60"
          >
            <FiDownload className="w-4 h-4" /> Excel
          </button>
        </div>

        {/* ── Window filter — every number below follows it ───────────────── */}
        <div className="flex flex-wrap items-center gap-2 animate-fade-in-up" style={{ animationDelay: '0.04s' }}>
          {PERIOD_CHIPS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPeriod(p.id)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
                period === p.id
                  ? 'border-violet-600 bg-violet-600 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'
              }`}
            >
              {p.id === 'custom' && <FiCalendar className="w-3 h-3" />}
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:border-violet-400 focus:outline-none" />
              <span className="text-xs text-slate-400">to</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:border-violet-400 focus:outline-none" />
              {(dateFrom || dateTo) && (
                <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-xs font-semibold text-slate-400 hover:text-rose-500 transition">
                  Clear
                </button>
              )}
            </div>
          )}
          {loading && <FiRefreshCw className="w-3.5 h-3.5 text-slate-400 animate-spin" />}
          {data?.window && (
            <span className="text-xs text-slate-400">
              {data.window.from || 'the beginning'} → {data.window.to}
            </span>
          )}
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}

        {/* ── Their numbers for this window ───────────────────────────────── */}
        <div className="flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
          <StatChip icon={FiMessageCircle} iconBg="bg-indigo-50" iconColor="text-indigo-500"
            label="Quoted" value={s.quoted ?? 0} valueColor="text-indigo-700" />
          <StatChip icon={FiPercent} iconBg="bg-violet-50" iconColor="text-violet-500"
            label={`Share of ${s.window_inquiries ?? 0} in`} value={`${s.handled_pct ?? 0}%`}
            valueColor="text-violet-700" />
          <StatChip icon={FiEdit3} iconBg="bg-fuchsia-50" iconColor="text-fuchsia-500"
            label="Corrected" value={s.edited ?? 0} valueColor="text-fuchsia-700" />
          <StatChip icon={FiCheckCircle} iconBg="bg-emerald-50" iconColor="text-emerald-500"
            label="Sold" value={s.sold ?? 0} valueColor="text-emerald-700" />
          <StatChip icon={FiTrendingUp} iconBg="bg-emerald-50" iconColor="text-emerald-500"
            label="Sold value" value={money(s.sold_value)} valueColor="text-emerald-700" />
          <StatChip icon={FiClock} iconBg="bg-amber-50" iconColor="text-amber-500"
            label="Avg time to quote" value={fmtMinutes(s.avg_minutes_to_quote)}
            valueColor="text-amber-700" />
        </div>

        {/* ── The deals themselves ────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.07s' }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <FiClipboard className="w-4 h-4 text-violet-500" />
              <h2 className="text-sm font-bold text-slate-800">
                Every deal they touched — {windowLabel}
              </h2>
            </div>
            <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-bold text-violet-700">{rows.length}</span>
          </div>

          {loading ? (
            <Spinner />
          ) : !rows.length ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mb-3">
                <FiBarChart2 className="w-7 h-7 text-violet-300" />
              </div>
              <p className="font-semibold text-slate-700 mb-1">Nothing in this window</p>
              <p className="text-sm text-slate-400">They didn't price or correct any inquiry here. Try a wider window.</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="lg:hidden divide-y divide-slate-50">
                {rows.map((r) => (
                  <div key={`${r.inquiry_id}-${r.role}`} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">{r.customer || 'Unknown customer'}</p>
                        <p className="text-xs text-slate-500 truncate">{r.car || '—'}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <RolePill role={r.role} />
                        <StagePill stage={r.stage} />
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 line-clamp-2">{r.parts || 'No parts listed'}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <span className="font-bold text-slate-700">
                        {r.quoted_price ? money(r.quoted_price) : 'No price'}
                      </span>
                      {r.sold && <span className="font-bold text-emerald-600">{money(r.sold_value)} sold</span>}
                      {r.minutes_to_quote !== null && r.minutes_to_quote !== undefined && (
                        <span className="text-amber-600 font-semibold">reply {fmtMinutes(r.minutes_to_quote)}</span>
                      )}
                      <span className="text-slate-400">{r.created_at}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[980px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['Came in', 'Customer', 'Car', 'Parts', 'Price', 'Reply in',
                        'Role', 'Stage', 'Sold value', 'Salesperson'].map((h) => (
                        <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3.5 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {rows.map((r) => (
                      <tr key={`${r.inquiry_id}-${r.role}`} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3.5 text-xs text-slate-500 whitespace-nowrap">{r.created_at}</td>
                        <td className="px-5 py-3.5">
                          <p className="text-sm font-semibold text-slate-800">{r.customer || '—'}</p>
                          <p className="text-xs text-slate-400">{r.customer_phone || ''}</p>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-600 whitespace-nowrap">{r.car || '—'}</td>
                        <td className="px-5 py-3.5 text-xs text-slate-500 max-w-[240px]">
                          <p className="line-clamp-2">{r.parts || '—'}</p>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <p className="text-sm font-bold text-slate-700">
                            {r.quoted_price ? money(r.quoted_price) : <span className="text-slate-300">—</span>}
                          </p>
                          {r.first_quoted_price && r.first_quoted_price !== r.quoted_price && (
                            <p className="text-[11px] text-slate-400 line-through">{money(r.first_quoted_price)}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-xs font-semibold text-amber-600 whitespace-nowrap">
                          {fmtMinutes(r.minutes_to_quote)}
                        </td>
                        <td className="px-5 py-3.5"><RolePill role={r.role} /></td>
                        <td className="px-5 py-3.5"><StagePill stage={r.stage} /></td>
                        <td className="px-5 py-3.5 text-sm font-bold text-emerald-600 whitespace-nowrap">
                          {r.sold ? money(r.sold_value) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate-500 whitespace-nowrap">{r.salesperson || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ── Day by day ──────────────────────────────────────────────────── */}
        {!loading && (data?.daily || []).length > 1 && (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.09s' }}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-800">Day by day</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {['Date', 'Touched', 'Priced', 'Corrected', 'Sold', 'Sold value'].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.daily.map((d) => (
                    <tr key={d.date}>
                      <td className="px-5 py-3 text-sm font-semibold text-slate-700 whitespace-nowrap">{d.date}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{d.touched}</td>
                      <td className="px-5 py-3 text-sm text-indigo-600 font-semibold">{d.priced}</td>
                      <td className="px-5 py-3 text-sm text-violet-600 font-semibold">{d.corrected}</td>
                      <td className="px-5 py-3 text-sm text-emerald-600 font-semibold">{d.sold}</td>
                      <td className="px-5 py-3 text-sm font-bold text-emerald-700">{money(d.sold_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default SupervisorAnalyticsPage;
